import Link from 'next/link';
import { JSX } from 'react';
import { AtIcon } from '../at-icon';
import { EIcons } from '../at-icon/icons';
import { EClickableTarget } from '@/components/atoms/at-clickable/at-clickable.types';
import classNames from 'classnames';
import { sendGTMNavigationEvent } from '@/components/helpers/analytics/gtm';
import { IAtarrowLinkProps } from './at-arrow.types';

/**
 * `AtArrowLink` is a styled link element with a right arrow icon, used to guide users
 * toward an action or destination. It supports optional label text and fires a GTM
 * navigation event on click.
 *
 * This component is primarily used inside `MlSearchLocationForm`.
 *
 * - Renders a right-facing arrow icon
 * - Triggers `sendGTMNavigationEvent` with label, type, and href
 * - Applies focus styles unless rendered as arrow-only
 */

export const AtArrowLink = ({
  label,
  href,
  aria_label,
}: IAtarrowLinkProps): JSX.Element => {
  const onlyArrow = label.length <= 0;

  return (
    <Link
      href={href}
      target={EClickableTarget.SELF}
      aria-label={aria_label}
      onClick={() => sendGTMNavigationEvent(label, 'at-arrow-link', href)}
      className={classNames(
        'h6-lg group inline-flex flex-row items-center justify-center p-2 pl-6 focus:outline-0',
        {
          'focus:rounded-30 focus:outline focus:outline-2 focus:outline-black':
            !onlyArrow,
        },
      )}
    >
      {label}
      <span className="w-63">
        <span className="inline-flex items-center justify-start rounded-80 px-4 py-2 text-24 transition-all group-hover:translate-x-2 group-hover:bg-skin-brand group-focus:translate-x-2 group-focus:bg-skin-brand">
          <AtIcon icon={EIcons.RIGHT_ARROW} />
        </span>
      </span>
    </Link>
  );
};
